/**
 * IELTS overall report model for the invitation report page.
 *
 * Unlike the per-skill report builders in ieltsSkills.mjs — which rebuild a
 * submission from its /progress-report rows — this one works from the
 * `definitions-detail` rows the invitation report page has already loaded, so
 * showing the aggregated report costs no extra requests. The trade-off is that
 * the bands shown here are the ones the backend stored per skill rather than
 * bands recomputed from the answers; if a stored score ever disagrees with the
 * per-skill detailed report, the detailed report is the authority.
 *
 * Mirrors sensay-assessment-admin src/utils/ieltsOverall.js so the candidate
 * and the examiner read the same numbers off the same rows.
 */

import {
  IELTS_CATEGORY_SKILLS,
  fractionToBand,
  meanBand,
  roundToHalfBand,
} from "./ieltsSkills.mjs";

/** Column order of the overall report, matching the official reporting order. */
export const IELTS_OVERALL_SKILLS = [
  "LISTENING",
  "READING",
  "WRITING",
  "SPEAKING",
];

/**
 * Band-to-other-test equivalences shown under the report. The CEFR, TOEIC
 * Listening+Reading and TOEFL columns are the same equivalences the TOEIC
 * consolidated report already publishes in its own comparison table, read from
 * the IELTS side so the two reports cannot drift apart. `maxBand` is the top of
 * each row's band range and is what ieltsComparisonRowIndex matches on.
 *
 * `toeicSw` is the TOEIC Speaking and Writing total (0-400) for the same CEFR
 * level: the speaking half is the speakingRange the TOEIC consolidated report
 * already uses per level (50-80 / 90-110 / 120-150 / 160-170 / 180-200) and the
 * writing half is ETS's published CEFR alignment for the writing section
 * (30-69 / 70-119 / 120-149 / 150-179 / 180-200), added together. The TOEIC
 * Speaking and Writing tests do not report a C2 level, so that row has no
 * equivalent — the same convention the Listening+Reading column already uses.
 */
export const IELTS_COMPARISON_ROWS = [
  {
    band: "1.0 - 1.5",
    cefr: "A1",
    toeic: "120 - 215",
    toeicSw: "80 - 149",
    toefl: "0 - 18",
    maxBand: 1.5,
  },
  {
    band: "2.0 - 3.5",
    cefr: "A2",
    toeic: "220 - 545",
    toeicSw: "160 - 229",
    toefl: "19 - 55",
    maxBand: 3.5,
  },
  {
    band: "4.0 - 5.0",
    cefr: "B1",
    toeic: "550 - 780",
    toeicSw: "240 - 299",
    toefl: "56 - 81",
    maxBand: 5,
  },
  {
    band: "5.5 - 6.5",
    cefr: "B2",
    toeic: "785 - 940",
    toeicSw: "310 - 349",
    toefl: "82 - 99",
    maxBand: 6.5,
  },
  {
    band: "7.0 - 8.0",
    cefr: "C1",
    toeic: "945 - 990",
    toeicSw: "360 - 400",
    toefl: "100 - 110",
    maxBand: 8,
  },
  {
    band: "8.5 - 9.0",
    cefr: "C2",
    toeic: "—",
    toeicSw: "—",
    toefl: "111 - 120",
    maxBand: 9,
  },
];

/**
 * Index of the comparison row a band falls in, or null when there is no band
 * to highlight. Bands below the bottom row still highlight it — band 0 means
 * "did not attempt", which is reported as the lowest tier rather than as its
 * own row.
 */
export function ieltsComparisonRowIndex(band) {
  if (!Number.isFinite(band)) {
    return null;
  }
  const index = IELTS_COMPARISON_ROWS.findIndex((row) => band <= row.maxBand);
  return index === -1 ? IELTS_COMPARISON_ROWS.length - 1 : index;
}

/** A skill row the candidate never opened contributes no band to the average. */
const EMPTY_SKILL = {
  band: null,
  name: null,
  definitionId: null,
  instanceId: null,
  started: false,
  startedAt: null,
  finishedAt: null,
};

/**
 * Reads a stored skill score as a band on the 0-9 scale.
 *
 * `displayScore` / `reviewScore` are not on a single scale across the product:
 * IELTS definitions report the band itself (6.5), while percentage-scored
 * definitions report 0-100. A value above the top of the band scale can only
 * be a percentage, so it is converted; anything within 0-9 is taken as a band
 * and snapped to the nearest half band.
 */
export function resolveIeltsBandFromScore(value) {
  const numeric = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(numeric) || numeric < 0) {
    return null;
  }
  return numeric > 9 ? fractionToBand(numeric / 100) : roundToHalfBand(numeric);
}

/** Earliest / latest of a list of date strings, ignoring the empty ones. */
function boundaryDate(values, pick) {
  const times = (values || [])
    .filter((value) => !!value)
    .map((value) => ({ value, time: new Date(value).getTime() }))
    .filter((entry) => Number.isFinite(entry.time));
  if (!times.length) {
    return null;
  }
  return times.reduce((best, entry) => (pick(entry.time, best.time) ? entry : best))
    .value;
}

/**
 * Overall report model from the invitation's `definitions-detail` rows.
 *
 * CANCELled rows are dropped, the same way the TOEIC report filters them out.
 * When an invitation carries more than one definition for the same skill (a
 * retake, or a definition swapped mid-flight) the higher band wins.
 *
 * @param {Array} definitionRows rows of
 *   public/assessment/invitation/{number}/definitions-detail
 * @returns {{skills: Array, band: number|null, hasSkills: boolean,
 *            startedAt: string|null, finishedAt: string|null,
 *            scoredCount: number}}
 */
export function buildIeltsOverallReport(definitionRows) {
  const bySkill = {};

  (definitionRows || []).forEach((row) => {
    const skill = IELTS_CATEGORY_SKILLS[row?.category];
    if (!skill) {
      return;
    }
    if (row?.invDefStatus === "CANCEL" || row?.status === "CANCEL") {
      return;
    }

    const candidate = {
      band: resolveIeltsBandFromScore(
        row?.displayScore === null || row?.displayScore === undefined
          ? row?.reviewScore
          : row?.displayScore,
      ),
      name: row?.name || null,
      definitionId: row?.idDefinition ?? null,
      instanceId: row?.idInstance ?? null,
      started: !!row?.idInstance,
      startedAt: row?.createdAt || null,
      finishedAt: row?.finishedAt || null,
    };

    const current = bySkill[skill];
    if (!current || (candidate.band ?? -1) > (current.band ?? -1)) {
      bySkill[skill] = candidate;
    }
  });

  const skills = IELTS_OVERALL_SKILLS.map((skill) => ({
    skill,
    ...EMPTY_SKILL,
    ...(bySkill[skill] || {}),
  }));

  return {
    skills,
    band: meanBand(skills.map((entry) => entry.band)),
    hasSkills: Object.keys(bySkill).length > 0,
    scoredCount: skills.filter((entry) => entry.band !== null).length,
    // Derived from the skill rows rather than the invitation: an invitation is
    // created when it is sent out, which can be long before the candidate
    // actually sat the first paper.
    startedAt: boundaryDate(
      skills.map((entry) => entry.startedAt),
      (time, best) => time < best,
    ),
    finishedAt: boundaryDate(
      skills.map((entry) => entry.finishedAt),
      (time, best) => time > best,
    ),
  };
}

/**
 * True when an invitation is an IELTS one and should therefore get the IELTS
 * aggregated report rather than the TOEIC consolidated one. The two reports
 * read different definition categories and score them on different scales, so
 * only one of them can produce a meaningful report for a given invitation.
 */
export function isIeltsInvitation(invitationDetail) {
  return String(invitationDetail?.assessmentType || "")
    .toUpperCase()
    .startsWith("IELTS");
}
